/**
 * `AuthAPI` — magic-link UI surface for the dashboard SPA.
 *
 * ADR-0018 ships the real implementation; the original ADR-0003 mock
 * is preserved as a test fixture per ADR-0018 §9. Three notable shape
 * changes vs. the pre-ADR-0018 contract:
 *
 *   - `signIn(email)` returns `Promise<void>` (was `Promise<{check_email: true}>`).
 *     The 204 anti-enumeration response (ADR-0011 §5.1) carries no
 *     body; the success-flip is a status-code-only signal. A 429
 *     (rate-limit) is thrown as {@link AuthRateLimitError}; any other
 *     failure surfaces as {@link AuthError} (soft-retry UX).
 *
 *   - `verify(token)` is REMOVED. Per ADR-0018 §6.2 the verify flow is
 *     a top-level browser navigation
 *     (`window.location.assign(`${API_ORIGIN}/v1/auth/verify?token=…`)`),
 *     not a JS-driven fetch. The dashboard's `<Verify />` view renders
 *     "Verifying…" while the navigation happens; the server responds
 *     with `303 + Set-Cookie + Location` and the browser commits the
 *     cookie + follows the redirect. No client-side verify call.
 *
 *   - `signOut()` is added. Calls `POST /v1/auth/sign-out` cross-origin
 *     (covered by ADR-0018 §2.4's CORS extension). Resolves on 204;
 *     throws {@link AuthError} otherwise. The server's clearing cookie
 *     carries `Domain=.sill.so` per ADR-0017 §4.3 so the browser
 *     drops the held cookie.
 *
 * The server-side mailer / token format / verification logic ship in
 * ADR-0011; the cross-origin posture in ADR-0016 / ADR-0017; the
 * dashboard client in ADR-0018. Every realistic implementation of
 * this interface must route through the security subagent before
 * production deploy per CLAUDE.md §7.
 */
/**
 * Thrown by {@link AuthAPI.signIn} when the server returns 429. The
 * `retry_after_minutes` is derived from the `Retry-After` response
 * header (CORS-safelisted; the dashboard's HTTP client reads it
 * without any `Access-Control-Expose-Headers` dance).
 */
export class AuthRateLimitError extends Error {
    retry_after_minutes;
    server_reason;
    server_body;
    constructor(retry_after_minutes, 
    /**
     * Optional server-supplied `reason` parsed from the 429 response body
     * (ADR-0103 — the catalog manual-sync trigger returns
     * `{error:'rate_limited', reason:'daily_cap'|'too_soon'}`). The auth
     * (sign-in) 429 carries no body reason and leaves this undefined; the
     * field is purely additive so the auth bag is unchanged.
     */
    server_reason, 
    /**
     * Opaque parsed 429 body for callers that need per-reason context
     * beyond `server_reason` (ADR-0263 §7.5 — `email_limit_reached`
     * carries `message`; `monthly_scan_limit_reached` carries
     * `used`/`limit`/`reset_at_iso`). Null when the body wasn't
     * JSON-parseable. Additive; unused by the auth bag.
     */
    server_body) {
        super('rate_limited');
        this.retry_after_minutes = retry_after_minutes;
        this.server_reason = server_reason;
        this.server_body = server_body;
        this.name = 'AuthRateLimitError';
    }
}
/**
 * Thrown by {@link AuthAPI} methods on any non-success / non-rate-
 * limited response (5xx, network failure, CORS denial, unexpected
 * status). The dashboard surfaces a soft-retry copy on this — it does
 * NOT flip success state. Sign-in's 204-is-success narrowness preserves
 * anti-enumeration; AuthError is the catch-all for everything else.
 */
export class AuthError extends Error {
    constructor(message) {
        super(message);
        this.name = 'AuthError';
    }
}
/**
 * Thrown by {@link AuthAPI.currentUser} when the server returns 401.
 * The bootstrap auth check (ADR-0018 §7) catches this and routes the
 * dashboard to the sign-in view; in-flight calls from other views
 * trigger the same routing via the `on_unauthorized` callback wired
 * into the HTTP client.
 */
export class AuthUnauthorizedError extends Error {
    constructor() {
        super('unauthorized');
        this.name = 'AuthUnauthorizedError';
    }
}
//# sourceMappingURL=auth-api.js.map